import { markRaw } from 'vue'
import { Document } from './utils/types'
import { DocumentSource } from './utils/enums'
import { Entity } from '../utils/common'
import { EntityEvent } from '../utils/enums'
import { EntityErrors } from '../utils/types'

import { DOCUMENT_DEFAULT_DATA, DOCUMENT_FIELD } from './utils/const'
import { EMPTY_VALUE_PLACEHOLDER } from '@/const/common'

import { Rule, rules } from '@/helpers/validate'
import { getAssetById } from '@/views/Analytics/utils/helpers'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useDocumentsStore } from '@/store/documents/index'
import { useDocumentsBunchStore } from '@/store/documents/bunch'
import { useModalsStore } from '@/store/modals'

import DocumentDialog from './components/DocumentDialog.vue'
import DocumentDrawer from './components/DocumentDrawer.vue'
import DocumentFormFile from './components/DocumentFormFile.vue'
import DocumentFormAsset from './components/DocumentFormAsset.vue'
import DocumentFormName from './components/DocumentFormName.vue'
import DocumentFormPath from './components/DocumentFormPath.vue'
import DocumentFormTags from './components/DocumentFormTags.vue'
import DocumentFormThumbnail from './components/DocumentFormThumbnail.vue'

export class DocumentClass extends Entity<Document> {
  private documentsStore
  private bunchStore
  private assetsBunchStore
  private modalsStore

  constructor(data: Partial<Document>, created = false) {
    super(data, DOCUMENT_DEFAULT_DATA, created)
    this.documentsStore = useDocumentsStore()
    this.bunchStore = useDocumentsBunchStore()
    this.assetsBunchStore = useAssetsBunchStore()
    this.modalsStore = useModalsStore()
    this.cachedData = { key: '' }
  }

  get isFile() {
    return [DocumentSource.ALLPOSIT, DocumentSource.ALLPOSIT_THUMBNAIL].includes(this.data.source)
  }

  get isThumbnail() {
    return this.data.source === DocumentSource.ALLPOSIT_THUMBNAIL
  }

  get getModal() {
    return markRaw(this.isDialog ? DocumentDialog : DocumentDrawer)
  }

  getFormName() {
    return DocumentFormName
  }

  getFormPath() {
    return DocumentFormPath
  }

  getFormFile() {
    return DocumentFormFile
  }

  getFormThumbnail() {
    return DocumentFormThumbnail
  }

  getFormAssetId() {
    return DocumentFormAsset
  }

  getFormTagsField() {
    return DocumentFormTags
  }

  validate() {
    const result: Record<string, Rule> = {
      name: rules.required,
    }
    if (this.isNew) {
      if (this.isFile) {
        result.filename = rules.required
      } else {
        result.path = rules.required
      }
    }
    return result
  }

  get displayAssetName() {
    this.prepareAssetName()
    return this.cachedData?.value
  }

  private removeFromBunch() {
    this.bunchStore.getList.delete(this.id)
  }

  private removeFromForms() {
    this.modalsStore.getList.delete(this.id)
  }

  remove() {
    this.removeFromBunch()
    this.removeFromForms()
  }

  private assigntToAsset(data: Document, prev?: Document) {
    if (prev?.asset_id === data.asset_id) return
    if (prev) {
      this.removeFromAsset(prev)
    }
    if (data.asset_id) {
      const asset = this.assetsBunchStore.getElementById(data.asset_id)
      if (!asset?.docs) return
      if (!asset.docs.includes(data.id)) {
        asset.docs.push(data.id)
      }
      asset?.update()
    }
  }

  private removeFromAsset(data: Document) {
    if (data.asset_id) {
      const asset = this.assetsBunchStore.getElementById(data.asset_id)
      if (!asset?.docs) return
      asset.docs = asset.docs.filter(id => id !== data.id)
      asset?.update()
    }
  }

  private prepareAssetName() {
    if (!this.assetsBunchStore?.getList.size) return
    const key = this.field<string>(DOCUMENT_FIELD.ASSET_ID).value
    if (this.cachedData.key !== key) {
      const asset = getAssetById(key, this.assetsBunchStore.getList)
      const value =
        asset?.field<string>(DOCUMENT_FIELD.NAME) || EMPTY_VALUE_PLACEHOLDER
      this.cachedData = { key, value }
    }
  }

  resetAssetId() {
    this.cachedData = {
      key: '',
      value: '',
    }
    this.prepareAssetName()
  }

  handleErrors(errors: EntityErrors) {
    return errors
  }

  async fetch() {
    try {
      const result = await this.documentsStore.fetchSingle(this.id)
      this.reset(result)
    } catch (e) {
      throw Error(e as string)
    }
  }

  async store() {
    try {
      const data = this.get()
      const result = await this.documentsStore.store(data)
      this.remove()
      if (result !== undefined) {
        this.assigntToAsset(result)
        this.reset(result)
        this.bunchStore.unshiftElement(this)
        this.callEvent(EntityEvent.STORED)
      }
    } catch (e) {
      throw Error(e as string)
    }
  }

  async update() {
    try {
      const result = await this.documentsStore.update(this.get())
      if (result === undefined) {
        this.cancel()
      } else {
        this.assigntToAsset(result, this.original)
        this.callEvent(EntityEvent.UPDATED)
        this.reset(result)
      }
    } catch (e) {
      throw Error(e as string)
    }
  }

  async destroy() {
    try {
      if (!this.isNew) {
        await this.documentsStore.destroy(this.id)
        this.callEvent(EntityEvent.DESTROYED)
      }
      this.remove()
    } catch (e) {
      throw Error(e as string)
    }
  }
}
